/* eslint-disable no-mixed-spaces-and-tabs */
import { tr } from "date-fns/locale";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useEffect, useMemo, useState } from "react";

import { mergeObjects } from "../../../../../../utils/helpers";
import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import {
	AllocationSettings,
	JobRole,
	LocationSkill,
} from "../../../../store/gat-admin-slice-types";

type Rule = {
	shiftAllocationTypeId: number;
	isDefault: boolean;
	jobIds: string;
};

interface ShiftAllocationRulesProps {
	locationData: any;
	setLocationData: any;
	creatingNewLocation: boolean;
}

const useShiftAllocationConfigController = ({
	locationData,
	setLocationData,
	creatingNewLocation,
}: ShiftAllocationRulesProps) => {
	const confirm = useConfirm();
	const {
		shiftAllocationTypes,
		allJobsByLocation,
		allJobsByLocationObject,
		allSkillsByLocation,
		allSkillsByLocationObject,
	} = useGatAdminConfigData();

	const [filteredJobsByLocation, setFilteredJobsByLocation] = useState<JobRole[]>([]);
	const [jobDialogOpen, setJobDialogOpen] = useState(-1);
	const [skillDialogOpen, setSkillDialogOpen] = useState(-1);
	const [seniorityDialogOpen, setSeniorityDialogOpen] =
		useState<boolean>(false);
	const [workcenterDialogOpen, setWorkcenterDialogOpen] =
		useState<boolean>(false);
	const [locationDialogOpen, setLocationDialogOpen] = useState<boolean>(false);
	const [primaryJobDialogOpen, setPrimaryJobDialogOpen] =
		useState<boolean>(false);
	const [ configurationDialogOpen, setConfigurationDialogOpen ] = useState<boolean>(false);

	const { allocationTypes, status, errorMessage } = shiftAllocationTypes;

	const [shiftAllocationRules, setShiftAllocationRules] = useState<
		AllocationSettings[]
	>([]);
	const [ initialShiftAllocationRules, setInitialShiftAllocationRules ] = useState<AllocationSettings[]>([]);

	useEffect(() => {
		try {
			// Remove the jobs that are already assigned to the allocation rules and assign it to filteredJobsByLocation
			const filteredJobs = allJobsByLocation.filter(
				(job: any) =>
					!shiftAllocationRules.find(
						(rule: AllocationSettings) =>
							rule.jobIds?.split(",").includes(job.id.toString())
					)
			);
			setFilteredJobsByLocation(filteredJobs ?? []);
		} catch (e) {
			console.log("Error", e);
		}
	}, [allJobsByLocation, shiftAllocationRules]);

	// A skill can only be linked to one allocation rule per location
	const filteredSkillsByLocation = useMemo(
		() =>
			allSkillsByLocation.filter(
				(skill: LocationSkill) =>
					!shiftAllocationRules.some((rule: AllocationSettings) =>
						rule.skillIds?.split(",").includes(skill.id.toString())
					)
			),
		[allSkillsByLocation, shiftAllocationRules]
	);

	useEffect(() => {
		const baseSettings = {
			jobIds: null,
			skillIds: null,
			locationId: locationData.id,
			defaultWindowSizeMins: 0,
			urgentWindowSizeMins: 0,
			tieBreakerProp: null,
			id: 0,
		};

		const newRules = creatingNewLocation
			? allocationTypes.map((type: AllocationSettings) => ({
					...type,
					...baseSettings,
					shiftAllocationTypeId: type.shiftAllocationTypeId,
			  }))
			: allocationTypes.map((type: AllocationSettings) => {
					const allocationSetting = locationData?.allocationSettings?.find(
						(setting: { shiftAllocationTypeId: number }) =>
							setting.shiftAllocationTypeId === type.shiftAllocationTypeId
					);

					if (!allocationSetting) {
						return {
							...type,
							...baseSettings,
							shiftAllocationTypeId: type.shiftAllocationTypeId,
						};
					}

					return {
						...mergeObjects(type, allocationSetting),
						locationId: locationData.id,
						id: allocationSetting.id,
					};
			  });
		setShiftAllocationRules(newRules);
		setInitialShiftAllocationRules(newRules);
	}, [locationData, allocationTypes, creatingNewLocation]);

	const openWorkcenterDialog = () => {
		setWorkcenterDialogOpen(true);
	};

	const closeWorkcenterDialog = () => {
		setWorkcenterDialogOpen(false);
	};

	const openSeniorityDialog = () => {
		setSeniorityDialogOpen(true);
	};

	const closeSeniorityDialog = () => {
		setSeniorityDialogOpen(false);
	};

	const openLocationDialog = () => {
		setLocationDialogOpen(true);
	};

	const closeLocationDialog = () => {
		setLocationDialogOpen(false);
	};

	const openPrimaryJobDialog = () => {
		setPrimaryJobDialogOpen(true);
	};

	const closePrimaryJobDialog = () => {
		setPrimaryJobDialogOpen(false);
	};

	const openConfigurationDialog = () => {
		setConfigurationDialogOpen(true);
	}

	const closeConfigurationDialog = () => {
		setConfigurationDialogOpen(false);
	}

	const handleDefaultChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, name: string) => {
			const { value } = event.target;
			confirm({
				title: "Are you sure?",
				description: "",
				content: `Are you sure you want to set the default shift allocation rules to ${name}?`,
			})
				.then(() => {
					// The default rule is the catch-all, so it keeps no links
					const newRules = shiftAllocationRules.map(
						(rule: AllocationSettings) =>
							rule.shiftAllocationTypeId === Number(value)
								? { ...rule, isDefault: true, jobIds: null, skillIds: null }
								: { ...rule, isDefault: false }
					);
					setShiftAllocationRules(newRules);
					setInitialShiftAllocationRules(newRules);
					setLocationData((prevData: any) => ({
						...prevData,
						allocationSettings: newRules,
					}));
				})
				.catch(() => {
					/* ... */
				});
		},
		[confirm, shiftAllocationRules, setLocationData]
	);

	const removeLinkedId = useCallback(
		(
			shiftAllocationTypeId: number,
			linkField: "jobIds" | "skillIds",
			linkedId: string
		) => {
			const newRules = shiftAllocationRules.map((rule: AllocationSettings) => {
				if (rule.shiftAllocationTypeId !== shiftAllocationTypeId) return rule;
				const remainingIds = (rule[linkField] ?? "")
					.split(",")
					.filter((id) => id && id !== linkedId)
					.join(",");
				return { ...rule, [linkField]: remainingIds || null };
			});
			setShiftAllocationRules(newRules);
			setInitialShiftAllocationRules(newRules);
			setLocationData((prevData: any) => ({
				...prevData,
				allocationSettings: newRules,
			}));
		},
		[shiftAllocationRules, setLocationData]
	);

	const handleJobRemove = useCallback(
		(shiftAllocationTypeId: number, jobId: string) =>
			removeLinkedId(shiftAllocationTypeId, "jobIds", jobId),
		[removeLinkedId]
	);

	const handleSkillRemove = useCallback(
		(shiftAllocationTypeId: number, skillId: string) =>
			removeLinkedId(shiftAllocationTypeId, "skillIds", skillId),
		[removeLinkedId]
	);

	return [
		{
			allocationTypes,
			jobDialogOpen,
			seniorityDialogOpen,
			status,
			errorMessage,
			shiftAllocationRules,
			allJobsByLocation,
			allJobsByLocationObject,
			workcenterDialogOpen,
			locationDialogOpen,
			primaryJobDialogOpen,
			configurationDialogOpen,
			initialShiftAllocationRules,
			filteredJobsByLocation,
			skillDialogOpen,
			allSkillsByLocation,
			allSkillsByLocationObject,
			filteredSkillsByLocation,
		},
		{
			handleDefaultChange,
			setJobDialogOpen,
			setSeniorityDialogOpen,
			setShiftAllocationRules,
			handleJobRemove,
			setSkillDialogOpen,
			handleSkillRemove,
			openWorkcenterDialog,
			closeWorkcenterDialog,
			openSeniorityDialog,
			closeSeniorityDialog,
			openLocationDialog,
			closeLocationDialog,
			openPrimaryJobDialog,
			closePrimaryJobDialog,
			openConfigurationDialog,
			closeConfigurationDialog,
		},
	];
};

export default useShiftAllocationConfigController;
